import * as React from "react";
import { Box, Container, Typography, Grid, Card, Chip, Stack } from "@mui/material";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import AirlineSeatReclineExtraIcon from "@mui/icons-material/AirlineSeatReclineExtra";

import C5C_5429 from "../../assets/images/C5C_5529.jpg";
import C5C_5420 from "../../assets/images/C5C_5520.jpg";
import C5C_5425 from "../../assets/images/C5C_5525.jpg";
import C5C_5546 from "../../assets/images/C5C_5546.jpg";
import C5C_5558 from "../../assets/images/C5C_5558.jpg";
import C5C_5508 from "../../assets/images/C5C_5508.jpg";
import C5C_5556 from "../../assets/images/C5C_5556.jpg";

const highlights = [
  {
    icon: <VerifiedUserIcon sx={{ color: "#1565C0" }} />,
    title: "Safe operations",
    text: "Every trip is managed with attention to boarding points, passenger records, and dependable dispatch coordination.",
  },
  {
    icon: <AccessTimeFilledIcon sx={{ color: "#1565C0" }} />,
    title: "Clear schedules",
    text: "Passengers can check departure details, fares, and route information before making a booking.",
  },
  {
    icon: <AirlineSeatReclineExtraIcon sx={{ color: "#1565C0" }} />,
    title: "Comfort-first travel",
    text: "The service is built to make long-distance travel feel simpler, more organized, and more comfortable.",
  },
];

const gallery = [C5C_5558, C5C_5546, C5C_5508, C5C_5425, C5C_5420, C5C_5429];

export default function About() {
  return (
    <Box id="about" sx={{ bgcolor: "#F4F7FB", pt: 0, pb: 10 }}>
      <Box
        sx={{
          background: "linear-gradient(150deg, #0D1B2A 0%, #1565C0 60%, #0288D1 100%)",
          px: 2,
          pt: { xs: 14, md: 16 },
          pb: { xs: 7, md: 9 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                icon={<DirectionsBusIcon sx={{ color: "inherit !important" }} />}
                label="About Vishmitha Highway Express"
                sx={{
                  mb: 2,
                  bgcolor: "rgba(255,255,255,0.14)",
                  color: "white",
                  fontWeight: 700,
                }}
              />
              <Typography variant="h2" sx={{ color: "white", fontWeight: 900, fontSize: { xs: "2.3rem", md: "3.6rem" }, lineHeight: 1.05, mb: 2 }}>
                Travel between Badulla and Colombo with more clarity and confidence.
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: { xs: "1rem", md: "1.08rem" }, maxWidth: 620 }}>
                Vishmitha Highway Express brings route details, departure timing, fare visibility, and seat booking into one reliable customer experience.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 20px 60px rgba(8,20,40,0.24)" }}>
                <Box
                  sx={{
                    height: { xs: 320, md: 460 },
                    backgroundImage: `url(${C5C_5556})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: "relative", zIndex: 1 }}>
        <Grid container spacing={2.5}>
          {highlights.map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <Card sx={{ borderRadius: 3, p: 3, height: "100%", border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 16px 36px rgba(13,27,42,0.08)" }}>
                <Box sx={{ mb: 2 }}>{item.icon}</Box>
                <Typography variant="h6" fontWeight={800} color="#0D1B2A" gutterBottom>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {item.text}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ pt: 8 }}>
        <Grid container spacing={5} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="overline" sx={{ color: "#1565C0", fontWeight: 800, letterSpacing: 1.2 }}>
              Our Service
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 900, color: "#0D1B2A", mb: 2, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              A smoother booking experience for long-distance passengers.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.9, mb: 2 }}>
              Many passengers struggle with inconsistent timing and limited information when planning intercity bus travel. Vishmitha Highway Express addresses that problem by making routes, fares, bus numbers, and departure details visible before the customer commits to a trip.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.9 }}>
              The goal is simple: give travelers a more organized way to book seats, confirm boarding details, and travel with fewer surprises on the day of departure.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}>
              {gallery.slice(0, 4).map((image, index) => (
                <Card key={index} sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "none" }}>
                  <Box
                    sx={{
                      height: { xs: 150, md: 190 },
                      backgroundImage: `url(${image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                </Card>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ pt: 8 }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="overline" sx={{ color: "#1565C0", fontWeight: 800, letterSpacing: 1.2 }}>
            Journey Standard
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 900, color: "#0D1B2A", fontSize: { xs: "2rem", md: "2.8rem" } }}>
            Built around safety, timing, and passenger comfort.
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          {gallery.slice(4).map((image, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Box
                  sx={{
                    height: { xs: 240, md: 320 },
                    backgroundImage: `url(${image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>

        <Stack spacing={2} sx={{ mt: 4, maxWidth: 940, mx: "auto", textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.9 }}>
            From booking a ticket to arriving at the destination, the service is designed to feel easier to understand and easier to trust. That is what makes Vishmitha Highway Express a strong choice for repeat travelers on this route.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}
